/**
 * Builds `category_id`, `parent_category_id`, `sub_category_id` query string.
 * Only defined, non-empty values are appended.
 */
function buildVendorProductQuery({
  categoryId,
  parentCategoryId,
  subCategoryId,
  page,
  limit,
  searchText,
} = {}) {
  const p = new URLSearchParams();
  if (page != null && String(page).trim() !== "") {
    p.append("page", String(page));
  }
  if (limit != null && String(limit).trim() !== "") {
    p.append("limit", String(limit));
  }
  if (searchText != null) {
    p.append("searchText", String(searchText));
  }
  if (categoryId != null && String(categoryId).trim() !== "") {
    p.append("category_id", String(categoryId));
  }
  if (parentCategoryId != null && String(parentCategoryId).trim() !== "") {
    p.append("parent_category_id", String(parentCategoryId));
  }
  if (subCategoryId != null && String(subCategoryId).trim() !== "") {
    p.append("sub_category_id", String(subCategoryId));
  }
  return p.toString();
}

export const endpoint = {
  getSignUp: "/signUp",
  signIn: "/signIn",
  changePassword: "/changePassword",
  resetPassword: "/user/resetPassword",
  verifyOtpSignup: "/verifyOtpSignup",
  forgotPassword: "/forgotPassword",
  getAdminProfile: "/getVendorProfile",
  getTerms_condition: "/getCMS",
  changeEmail: "/changeEmail",
  changePhone: "/changePhone",
  changePhoneVerifyOtp: "/changePhoneVerifyOtp",
  changePassword: "/changePassword",
  changeEmailVerifyOtp: "/changeEmailVerifyOtp",
  registerVendor: "/registerVendor",
  logout: "/logout",

  getCms: "/getCMS",
  /** GET — vendor FAQ list (`data.faqs`). */
  getVendorFaq: "/vendor/faq",

  getServices: "/services",
  getVendorServicesList: "/vendor/vendor-service-list",
  /** GET — `data.documents` + `data.service_documents` (per `business_type_id`). */
  getVendorDocuments: "/getVendorDocuments",
  /** GET — saved bank account for vendor (shape may be object or array in `data`). */
  getBank: "/getBank",
  /** POST — add/update bank (body matches bank form fields). */
  addBank: "/addBank",
  getSubCategories: (serviceId) => `/subcategory/${serviceId}`,
  // create / list service listing
  createServiceListing: "/listings",
  updateServiceListing: (listingId) => `/listings/${listingId}`,
  getTagsbyID: (serviceId) => `/tags/service/${serviceId}`,
  viewService: (id) => `/listings/${id}`,
  getEventTypes: "/event-types",
  getVendorListings: (serviceId) => `/listings?service_id=${serviceId}`,
  /** Same listings API as getVendorListings, without `service_id`. */
  getVendorListingsAll: (page = 1, limit = 1000) =>
    `/listings?page=${page}&limit=${limit}`,
  /** Rejected-services page: `GET /listings?...&approve_status=rejected` */
  getVendorRejectedListings: (page = 1, limit = 1000) =>
    `/listings?page=${page}&limit=${limit}&approve_status=rejected`,
  getVendorServicesByUserId: (userId) => `/listings/${userId}`,
  deleteServiceListing: (listingId) => `/listings/${listingId}`,
  requestListingApproval: (listingId) =>
    `/listings/${listingId}/request-approval`,
  uploadMultipleFiles: "/upload/multiple",
  uploadSingleFile: "/upload/single",
  getLable: "/labels",
  getVendorInquiry: "/enquiry",
  updateInquiryStatus: (inquiryId) => `/enquiry/${inquiryId}/status`,
  getBookings: "/booking",
  getVendorCateringBookings: "/booking/getVendorCateringBookings",
  getVendorCateringBookingById: (bookingId) =>
    `/booking/getVendorCateringBookingById/${encodeURIComponent(String(bookingId))}`,
  /** @deprecated Prefer updateVendorBookingStatus — path id must be vendor_booking_id */
  updateBookingStatus: (bookingId) =>
    `/booking/${encodeURIComponent(String(bookingId).trim())}/status`,
  /** PUT mark service complete — `vendor_booking_id` from booking payload (not customer booking _id). */
  updateVendorBookingStatus: (vendorBookingId) =>
    `/booking/${encodeURIComponent(String(vendorBookingId).trim())}/status`,
  /** POST body: { booking_id, service_category_id, service_type, title, description } */
  createHelpTicket: "/helpTicket/create",
  /** Query params: `page`, `limit` (adjust if backend uses different names). */
  helpTicketList: (page = 1, limit = 10) =>
    `/helpTicket/list?page=${encodeURIComponent(String(page))}&limit=${encodeURIComponent(String(limit))}`,
  helpTicketGetByBookingId: (bookingId) =>
    `/helpTicket/getTicketById?booking_id=${encodeURIComponent(String(bookingId))}`,
  /** POST body: `{ ticket_id, message }` */
  helpTicketVendorReply: "/helpTicket/vendorReply",
  /** POST body: `{ ticket_id }` */
  helpTicketClose: "/helpTicket/close",
  /** POST body: `{ ticket_id }` */
  helpTicketReopen: "/helpTicket/reopen",

  /**
   * Full route on server: .../v1/vendor/product/parentCategories
   * REACT_APP_API must already end with /v1/vendor (see .env) — do NOT prefix /vendor again.
   */
  vendorProductParentCategories: "/product/parentCategories",

  /**
   * Rent listing tabs (Men, Women, Decor).
   * GET .../v1/vendor/rent/rentParentCategory
   * Response: `{ code, data: [ { _id, name, status } ] }`.
   */
  vendorRentProductCategories: "/rent/rentParentCategory",

  /**
   * Rent parent hierarchy: sub_categories + nested sub_subcategories.
   * GET .../v1/vendor/rent/rentParentSubCategory?parent_category_id=
   */
  vendorRentParentSubCategory: (parentCategoryId) =>
    `/rent/rentParentSubCategory?parent_category_id=${encodeURIComponent(
      parentCategoryId
    )}`,

  /**
   * Rent product detail / field master data for a leaf category.
   * GET .../v1/vendor/rent/rentProductMasterData?parent_category_id=&sub_category_id=&sub_subcategory_id=
   */
  vendorRentProductMasterData: ({
    parentCategoryId,
    subCategoryId,
    subSubcategoryId,
  } = {}) => {
    const p = new URLSearchParams();
    if (parentCategoryId != null && String(parentCategoryId).trim() !== "") {
      p.append("parent_category_id", String(parentCategoryId));
    }
    if (subCategoryId != null && String(subCategoryId).trim() !== "") {
      p.append("sub_category_id", String(subCategoryId));
    }
    if (subSubcategoryId != null && String(subSubcategoryId).trim() !== "") {
      p.append("sub_subcategory_id", String(subSubcategoryId));
    }
    const q = p.toString();
    return q
      ? `/rent/rentProductMasterData?${q}`
      : "/rent/rentProductMasterData";
  },

  /**
   * Rent add product.
   * POST .../v1/vendor/rent/addRentProduct
   */
  vendorRentAddProduct: "/rent/addRentProduct",

  /**
   * Rent product listing.
   * GET .../v1/vendor/rent/listOfRentProduct?page=&limit=&searchText=&parent_category_id=
   */
  vendorRentProductList: ({
    page = 1,
    limit = 10,
    searchText = "",
    parentCategoryId,
  } = {}) => {
    const p = new URLSearchParams();
    p.append("page", String(page ?? 1));
    p.append("limit", String(limit ?? 10));
    p.append("searchText", searchText != null ? String(searchText) : "");
    if (parentCategoryId != null && String(parentCategoryId).trim() !== "") {
      p.append("parent_category_id", String(parentCategoryId).trim());
    }
    return `/rent/listOfRentProduct?${p.toString()}`;
  },

  /**
   * Rent product detail.
   * GET .../v1/vendor/rent/getRentProduct/{productId}
   */
  vendorRentProductDetails: (productId) =>
    `/rent/getRentProduct/${encodeURIComponent(String(productId))}`,

  /**
   * Rent update product.
   * PUT .../v1/vendor/rent/rentProduct/{productId}
   */
  vendorRentUpdateProduct: (productId) =>
    `/rent/rentProduct/${encodeURIComponent(String(productId))}`,

  /** Subcategories for a parent (shop Men / Women only in UI). */
  vendorProductSubCategories: (parentCategoryId) =>
    `/product/subCategories?parent_category_id=${encodeURIComponent(parentCategoryId)}`,

  /**
   * Categories under parent. Pass any of: categoryId, parentCategoryId, subCategoryId.
   */
  vendorProductCategories: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/categories?${q}` : "/product/categories";
  },

  vendorProductTags: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/tags?${q}` : "/product/tags";
  },

  /** Brands — only `parent_category_id` (no category_id / sub_category_id). */
  vendorProductBrands: (opts = {}) => {
    const parentId =
      opts.parentCategoryId ??
      opts.parent_category_id ??
      null;
    const p = new URLSearchParams();
    if (parentId != null && String(parentId).trim() !== "") {
      p.append("parent_category_id", String(parentId).trim());
    }
    const q = p.toString();
    return q ? `/product/brands?${q}` : "/product/brands";
  },

  vendorProductSizes: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/sizes?${q}` : "/product/sizes";
  },

  vendorProductFabrics: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/fabrics?${q}` : "/product/fabrics";
  },

  vendorProductColours: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/colours?${q}` : "/product/colours";
  },

  vendorProductMaterials: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/materials?${q}` : "/product/materials";
  },

  /** Backend path spelling */
  vendorProductOccasions: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/ocasions?${q}` : "/product/ocasions";
  },

  vendorProductOrnamentations: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/ornamentations?${q}` : "/product/ornamentations";
  },

  vendorProductPrintAndPattern: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/printAndPattern?${q}` : "/product/printAndPattern";
  },

  /** Patterns catalog (Men clothing `patterns` field) — scoped like other product lookups. */
  vendorProductPatterns: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/patterns?${q}` : "/product/patterns";
  },

  /** Skincare / Haircare Concern catalog → payload `concern_id`. */
  vendorProductConcerns: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/concerns?${q}` : "/product/concerns";
  },

  /** Skincare / Haircare Preference catalog → payload `preference_id` (wire when API is live). */
  vendorProductPreferences: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/preferences?${q}` : "/product/preferences";
  },

  vendorProductTypes: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/types?${q}` : "/product/types";
  },
  /**
   * GET every master catalog for one category scope in a single response, instead of
   * one request per attribute. Requires `parent_category_id` and `category_id`.
   */
  vendorShopProductMasters: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/shop/productMasters?${q}` : "/shop/productMasters";
  },
  vendorProductList: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/list?${q}` : "/product/list";
  },
  /** GET / PUT / DELETE single product — `/product/{product_id}`. */
  vendorProductDetails: (productId) => `/product/${encodeURIComponent(String(productId))}`,
  vendorUpdateProduct: (productId) => `/product/${encodeURIComponent(String(productId))}`,
  vendorDeleteProduct: (productId) => `/product/${encodeURIComponent(String(productId))}`,
  vendorAddProduct: "/product/addProduct",
  /** GET /v1/vendor/product/offers/active */
  vendorProductOffersActive: "/product/offers/active",
  /** GET /v1/vendor/product/inventory — variant stock list (optional type=shop|rent, search, page, limit) */
  vendorProductInventory: (opts = {}) => {
    const p = new URLSearchParams();
    const type = String(opts.type ?? opts.catalog ?? "").trim().toLowerCase();
    if (type === "shop" || type === "rent") p.set("type", type);
    if (opts.search != null && String(opts.search).trim()) {
      p.set("search", String(opts.search).trim());
    }
    if (opts.page != null && opts.page !== "") p.set("page", String(opts.page));
    if (opts.limit != null && opts.limit !== "") p.set("limit", String(opts.limit));
    const q = p.toString();
    return q ? `/product/inventory?${q}` : "/product/inventory";
  },

  /** GET delivery pincodes — requires category scope (`parent_category_id`, etc.). */
  vendorProductPincode: (opts = {}) => {
    const q = buildVendorProductQuery(opts);
    return q ? `/product/pincode?${q}` : "/product/pincode";
  },
  /** GET /v1/vendor/location — vendor delivery location catalog. */
  vendorLocation: "/location",
  /** POST `{ code }` to add a delivery pincode. */
  vendorAddPincode: "/pincode",
  /** GET /v1/vendor/orders/shop?status_group=pending|ongoing|shipping|delivered|cancelled|rejected|returns&page=&limit= */
  vendorShopOrders: ({ statusGroup = "pending", page = 1, limit = 10, search = "" } = {}) => {
    const p = new URLSearchParams();
    p.set("status_group", String(statusGroup || "pending"));
    p.set("page", String(page || 1));
    p.set("limit", String(limit || 10));
    if (search != null && String(search).trim()) {
      p.set("search", String(search).trim());
    }
    return `/orders/shop?${p.toString()}`;
  },
  /** GET /v1/vendor/orders/shop/:shopOrderId */
  vendorShopOrder: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}`,
  /** POST /v1/vendor/orders/shop/:shopOrderId/suborder/accept */
  vendorShopSuborderAccept: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/suborder/accept`,
  /** POST /v1/vendor/orders/shop/:shopOrderId/suborder/reject — body: { reason, reason_note } */
  vendorShopSuborderReject: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/suborder/reject`,
  /** PUT body: { order_status } e.g. ready_to_ship */
  vendorShopSuborderStatus: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/suborder/status`,
  /** POST body: { status: approved|rejected|completed, vendor_note? } */
  vendorShopReturnStatus: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/return/status`,
  /** POST initiate refund for suborder */
  vendorShopSuborderRefundInitiate: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/suborder/refund/initiate`,
  /** @deprecated use vendorShopReturnStatus */
  vendorShopReturnApprove: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/return/approve`,
  /** @deprecated use vendorShopReturnStatus */
  vendorShopReturnReject: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/return/reject`,
  /** @deprecated use vendorShopReturnStatus */
  vendorShopReturnReplace: (shopOrderId) =>
    `/orders/shop/${encodeURIComponent(String(shopOrderId).trim())}/return/replace`,

  /** GET ?page=&limit=&inquiry_tab=new|accepted|rejected — vendor package enquiry line items */
  vendorPackageEnquiryLineItems: ({ page = 1, limit = 10, inquiryTab = "new" } = {}) => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("limit", String(limit));
    p.set("inquiry_tab", String(inquiryTab || "new"));
    return `/package-enquiry/line-items?${p.toString()}`;
  },
  vendorPackageEnquiryLineItemById: (lineItemId) =>
    `/package-enquiry/line-items/${encodeURIComponent(String(lineItemId).trim())}`,
  /** PUT body or query: vendor line-item accept / reject */
  vendorPackageEnquiryLineItemStatus: (lineItemId) =>
    `/package-enquiry/line-items/${encodeURIComponent(String(lineItemId).trim())}/status`,

  /** POST form-data — vendor payout dispute (after completed booking) */
  vendorPayoutRequestCreate: "/payout-request/create",
  /** GET ?request_tab=&page=&limit= */
  vendorPayoutRequestList: "/payout-request/list",
  /** GET ?request_id= */
  vendorPayoutRequestDetail: "/payout-request/detail",
  vendorPayoutRequestReply: "/payout-request/reply",
  vendorPayoutRequestReopen: "/payout-request/reopen",
  vendorPayoutRequestClose: "/payout-request/close",
};
